import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { ApiResponse } from '../shared/models';

// ── Interfaces matching backend IntegrationMonitorDto ──

export interface IntegrationMonitorDto {
  summary: IntegrationMonitorSummary;
  tenants: TenantIntegrationStatus[];
}

export interface IntegrationMonitorSummary {
  totalIntegrations: number;
  activeIntegrations: number;
  inactiveIntegrations: number;
  successfulLast24h: number;
  failedLast24h: number;
  partialSuccessLast24h: number;
  neverExecuted: number;
  totalTenants: number;
  tenantsWithErrors: number;
  failedPrevious24h: number;
  successfulPrevious24h: number;
  mutedCount: number;
  criticalCount: number;
  warningCount: number;
}

export interface TenantIntegrationStatus {
  domainID: number;
  domainName: string;
  databaseName: string;
  totalIntegrations: number;
  activeIntegrations: number;
  failedLast24h: number;
  hasErrors: boolean;
  connectionError?: string;
  integrations: IntegrationStatusItem[];
}

export interface IntegrationStatusItem {
  integrationID: number;
  integrationName: string;
  description?: string;
  integrationTypeName: string;
  category?: string;
  isActive: boolean;
  scheduleType?: string;
  lastExecutedDate?: string;
  lastExecutionStatus?: string;
  lastRecordsProcessed?: number;
  lastRecordsSucceeded?: number;
  lastRecordsFailed?: number;
  lastRecordsSkipped?: number;
  lastExecutionDurationMs?: number;
  lastErrorMessage?: string;
  lastSuccessfulRunDate?: string;
  nextScheduledDate?: string;
  notificationEmails?: string;
  alertLevel?: string;
  isAcked: boolean;
  ackedUntil?: string;
  ackedBy?: string;
  ackedReason?: string;
  healthStatus: string;
}

export interface IntegrationExecutionHistoryItem {
  executionID: number;
  executionStartDate: string;
  executionEndDate?: string;
  status?: string;
  recordsProcessed?: number;
  recordsSucceeded?: number;
  recordsFailed?: number;
  recordsSkipped?: number;
  executionDurationMs?: number;
  errorMessage?: string;
  triggeredBy?: string;
}

export interface IntegrationAckRequest {
  hours: number;
  reason?: string;
}

@Injectable({ providedIn: 'root' })
export class SystemIntegrationMonitorService {
  private get apiUrl(): string {
    return `${this.configService.get('apiBaseUrl')}/SystemIntegrationMonitor`;
  }

  // HttpClient that bypasses MsalInterceptor — used for anonymous TV endpoint
  private rawHttp: HttpClient;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    handler: HttpBackend
  ) {
    this.rawHttp = new HttpClient(handler);
  }

  getMonitorData(): Observable<IntegrationMonitorDto> {
    return this.http.get<any>(this.apiUrl).pipe(
      map((response: any) => {
        if (!response?.success) {
          throw new Error(response?.errorMessage?.[0]?.message || 'API returned success=false');
        }
        return response.result;
      })
    );
  }

  getMonitorDataByToken(token: string): Observable<IntegrationMonitorDto> {
    return this.rawHttp.get<any>(`${this.apiUrl}/tv`, { params: { token } }).pipe(
      map((response: any) => {
        if (!response?.success) {
          throw new Error(response?.errorMessage?.[0]?.message || 'Invalid or missing dashboard token');
        }
        return response.result;
      })
    );
  }

  getHistory(domainId: number, integrationId: number, count: number = 5): Observable<ApiResponse<IntegrationExecutionHistoryItem[]>> {
    return this.http.get<ApiResponse<IntegrationExecutionHistoryItem[]>>(
      `${this.apiUrl}/${domainId}/integrations/${integrationId}/history?count=${count}`
    );
  }

  ackIntegration(domainId: number, integrationId: number, hours: number, reason?: string): Observable<ApiResponse<boolean>> {
    const request: IntegrationAckRequest = { hours, reason };
    return this.http.post<ApiResponse<boolean>>(
      `${this.apiUrl}/${domainId}/integrations/${integrationId}/ack`, request
    );
  }

  unackIntegration(domainId: number, integrationId: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(
      `${this.apiUrl}/${domainId}/integrations/${integrationId}/ack`
    );
  }
}
